/* Whitehouse product-page enhancements v20.
 * Quote & wishlist lists use browser localStorage (no server-side submission).
 * Buy Now uses the standard Shopify Ajax cart endpoint; native Add to Cart remains unchanged.
 */
(() => {
  'use strict';
  const QKEY = 'whl-quote-list-v20';
  const WKEY = 'whl-wishlist-v20';
  const storage = {
    read(key) { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (_) { return []; } },
    write(key, data) { try { localStorage.setItem(key, JSON.stringify(data)); return true; } catch (_) { return false; } }
  };
  const text = value => String(value ?? '').trim();
  const rootPath = () => (window.Shopify?.routes?.root || '/');
  const dom = (root, selector) => root.querySelector(selector);
  const byAttr = (root, selector) => root.querySelectorAll(selector);
  function money(cents, currency) {
    try { return new Intl.NumberFormat('en-ZA', {style:'currency',currency:currency||'ZAR',minimumFractionDigits:2}).format(cents/100); }
    catch (_) { return 'R ' + (cents / 100).toFixed(2); }
  }
  function esc(s) { return String(s || '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch])); }
  function init(scope=document) {
    const mains = scope.matches?.('[data-whl-main-product]') ? [scope] : [...scope.querySelectorAll('[data-whl-main-product]')];
    mains.forEach(main => {
      if (main.dataset.whlV20Active === '1') return;
      const dataScript = dom(main,'[data-whl-v20-data]');
      if (!dataScript) return;
      let data;
      try { data = JSON.parse(dataScript.textContent); } catch (err) { console.error('Whitehouse v20: invalid product configuration',err); return; }
      main.dataset.whlV20Active = '1';
      const variantMap = new Map((data.variants||[]).map(v => [String(v.id),v]));
      const quoteDialog = dom(main,'[data-whl-quote-dialog]');
      const currentId = () => {
        const hidden = dom(main,'form[data-type="add-to-cart-form"] input[name="id"]') || dom(main,'.product-variant-id');
        const id = hidden && !hidden.disabled ? hidden.value : hidden?.value || data.variantId;
        return String(id || '');
      };
      const currentVariant = () => variantMap.get(currentId()) || variantMap.get(String(data.variantId)) || data.variants?.[0];
      const currentQuantity = () => {
        const input = dom(main,'.quantity__input[name="quantity"]');
        const qty = Number(input?.value || 1);
        const min = Math.max(1, Number(input?.min || 1));
        const max = Number(input?.max);
        const clamped = Math.max(min, Math.floor(Number.isFinite(qty) && qty > 0 ? qty : min));
        return Number.isFinite(max) && max > 0 ? Math.min(clamped,max) : clamped;
      };
      const statusEl = dom(main,'[data-whl-enhancement-status]');
      let statusTimer;
      const notify = (msg) => {
        if (!statusEl) return;
        statusEl.textContent = msg;
        statusEl.classList.add('is-active');
        clearTimeout(statusTimer);
        statusTimer = setTimeout(() => statusEl.classList.remove('is-active'), 3800);
      };
      const quoteList = () => Array.isArray(storage.read(QKEY)) ? storage.read(QKEY) : [];
      const savedWish = () => Array.isArray(storage.read(WKEY)) ? storage.read(WKEY) : [];
      function countUpdate() {
        const count = quoteList().reduce((sum,item) => sum + Number(item.quantity||0),0);
        byAttr(main,'[data-whl-quote-count]').forEach(el => {el.textContent = String(count);});
        const wish = dom(main,'[data-whl-wishlist]');
        if (wish) {
          const active = savedWish().some(x => String(x.productId) === String(data.id));
          wish.setAttribute('aria-pressed',active?'true':'false');
          wish.setAttribute('aria-label',active?'Remove product from wishlist':'Add product to wishlist');
          wish.title = active?'Remove from wishlist':'Save to wishlist';
        }
      }
      const addQuote = () => {
        const v = currentVariant();
        if (!v) return notify('Choose a product option first.');
        const items = quoteList();
        const key = `${data.id}:${v.id}`;
        const qty = currentQuantity();
        const match = items.find(x=>x.key===key);
        if (match) match.quantity += qty;
        else items.push({key,productId:data.id,variantId:v.id,title:data.title,variant:v.title,sku:v.sku,quantity:qty,url:data.url});
        if (!storage.write(QKEY,items)) return notify('This browser could not save the quotation list. Please enable site storage.');
        countUpdate();
        notify(`${qty} ${qty===1?'unit':'units'} saved to your quotation list.`);
      };
      const renderQuote = () => {
        if(!quoteDialog) return;
        const items=quoteList();
        const wrap=dom(quoteDialog,'[data-whl-quote-items]');
        if(!wrap) return;
        if(!items.length) wrap.innerHTML='<p>Your quotation list is empty.</p>';
        else wrap.innerHTML=items.map(x=>`<div class="whl-quote-item"><div><strong>${esc(x.title)}</strong><span>Option: ${esc(x.variant||'Standard')} · SKU: ${esc(x.sku||'Not provided')} · Quantity: ${Number(x.quantity)||1}</span></div><button type="button" data-whl-quote-remove="${esc(x.key)}" aria-label="Remove ${esc(x.title)}">Remove</button></div>`).join('');
        const send=dom(quoteDialog,'[data-whl-quote-email]');
        if(send){
          const email=text(data.email);
          const body = 'Hello Whitehouse Lighting & Electrical,\n\nPlease quote on the following items and confirm availability, delivery and any volume pricing:\n\n'+items.map((x,i)=>`${i+1}. ${x.title}\n   Option: ${x.variant||'Standard'}\n   SKU: ${x.sku||'Not provided'}\n   Quantity: ${x.quantity}\n   Page: ${location.origin}${x.url}`).join('\n\n')+'\n\nMy name:\nDelivery area:\nContact number:\n';
          send.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent('Whitehouse product quotation request')}&body=${encodeURIComponent(body)}`;
          send.setAttribute('aria-disabled',(!items.length||!email)?'true':'false');
          send.textContent = !email?'Store email not configured':items.length?'Prepare quote email':'Add products to prepare email';
          send.style.pointerEvents = (!items.length||!email)?'none':'';
          send.style.opacity = (!items.length||!email)?'.55':'1';
        }
      };
      const openQuote = () => {
        if(!quoteDialog) return notify('Quote viewer is not available on this page.');
        renderQuote();
        if(typeof quoteDialog.showModal === 'function') quoteDialog.showModal();
        else quoteDialog.setAttribute('open','');
      };
      const toggleWishlist = () => {
        let items=savedWish();
        if(items.some(x=>String(x.productId)===String(data.id))){
          items=items.filter(x=>String(x.productId)!==String(data.id));
          if(!storage.write(WKEY,items)) return notify('Wishlist could not be updated in this browser.');
          notify('Removed from your wishlist.');
        }else{
          items.push({productId:data.id,title:data.title,url:data.url,image:data.image});
          if(!storage.write(WKEY,items)) return notify('Wishlist could not be saved in this browser.');
          notify('Added to your wishlist on this device.');
        }
        countUpdate();
      };
      function updateVariant() {
        const v=currentVariant();
        if(!v)return;
        const plan=dom(main,'[data-whl-stitch-plan]');
        if (plan) {
          const cents=Number(v.price||0)*currentQuantity(), eligible=cents>0 && cents<=2500000 && !!data.stitchEnabled;
          plan.hidden=!eligible;
          [2,4,6].forEach(n=>{const el=dom(plan,`[data-whl-stitch-${n}]`);if(el)el.textContent=money(Math.round(cents/n),data.currency);});
        }
        const stock=dom(main,'[data-whl-faq-stock]');
        if(stock) stock.textContent=v.available?'This selected option is currently listed as available to order. Check live availability in the purchase section before checkout.':'This selected option is currently unavailable. Choose another option or contact Whitehouse.';
      }
      countUpdate();updateVariant();
      const form=dom(main,'form[data-type="add-to-cart-form"]');
      // Capture Buy Now to use a single verified action instead of competing with other button click listeners.
      main.addEventListener('click',async e => {
        const target=e.target.closest('button,[data-whl-quote-email]');
        if(!target||!main.contains(target))return;
        if(target.matches('[data-whl-quote-add]')) { e.preventDefault();addQuote(); }
        else if(target.matches('[data-whl-quote-view]')) { e.preventDefault();openQuote(); }
        else if(target.matches('[data-whl-quote-close]')){e.preventDefault();quoteDialog?.close?.();}
        else if(target.matches('[data-whl-quote-clear]')){e.preventDefault();storage.write(QKEY,[]);countUpdate();renderQuote();}
        else if(target.matches('[data-whl-wishlist]')){e.preventDefault();toggleWishlist();}
        else if(target.matches('[data-whl-quote-remove]')){
          e.preventDefault();const key=target.getAttribute('data-whl-quote-remove');
          storage.write(QKEY,quoteList().filter(x=>x.key!==key));countUpdate();renderQuote();
        }
      });
      // Buy Now uses checkout with the currently selected product and quantity.
      main.addEventListener('click',async (e) => {
        const button=e.target.closest('[data-whl-buy-now]');
        if(!button||!main.contains(button))return;
        e.preventDefault();e.stopImmediatePropagation();
        if(button.disabled||button.dataset.whlProcessing==='true')return;
        const v=currentVariant();
        if(!v||!v.available)return notify('This option is not currently available to purchase.');
        if(!form)return notify('Product form is missing. Unable to start checkout.');
        const qty=currentQuantity();
        if(!Number.isFinite(qty)||qty<1)return notify('Choose a valid quantity.');
        button.dataset.whlProcessing='true';button.disabled=true;const previous=button.innerHTML;button.textContent='Preparing checkout…';
        try{
          const payload=new FormData(form);
          payload.set('id',String(v.id));payload.set('quantity',String(qty));
          const res=await fetch(rootPath()+'cart/add.js',{method:'POST',body:payload,headers:{'Accept':'application/json'},credentials:'same-origin'});
          if(!res.ok){const err=await res.json().catch(()=>({}));throw new Error(err.description||err.message||'Could not add this item to checkout.');}
          location.assign('/checkout');
        } catch(err){console.error(err);notify(err.message||'Checkout could not be started. Please use Add to Cart.');}
        finally{button.dataset.whlProcessing='false';button.disabled=false;button.innerHTML=previous;}
      },true);
      const variantInput=dom(main,'form[data-type="add-to-cart-form"] input[name="id"]');
      if(variantInput){const observer=new MutationObserver(updateVariant);observer.observe(variantInput,{attributes:true,attributeFilter:['value','disabled']});}
      main.addEventListener('change',e=>{if(e.target.matches('select,input[type="radio"],input[name="id"],input[name="quantity"]'))queueMicrotask(updateVariant);});
      main.addEventListener('input',e=>{if(e.target.matches('input[name="quantity"]'))queueMicrotask(updateVariant);});
      main.addEventListener('click',e=>{if(e.target.closest('.quantity__button'))setTimeout(updateVariant,0);});
      quoteDialog?.addEventListener('click',e=>{if(e.target===quoteDialog)quoteDialog.close();});
    });
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>init()); else init();
  document.addEventListener('shopify:section:load',ev=>init(ev.target));
})();
